import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Instagram, Youtube, Mail, Facebook, Linkedin } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useClickTracking } from "@/hooks/useClickTracking";
import { useTranslation } from "react-i18next";
import { useMultilingualContent } from "@/hooks/useMultilingualContent";

interface SocialMediaPlatform {
  platform_name: string;
  order_position: number;
  is_visible: boolean;
  is_featured: boolean;
}

interface HeroSectionProps {
  onContactClick: () => void;
}

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

export const HeroSection = ({ onContactClick }: HeroSectionProps) => {
  const { t, i18n } = useTranslation();
  const { transformContent } = useMultilingualContent();
  const { trackClick } = useClickTracking();
  const [isLoaded, setIsLoaded] = useState(false);
  const [socialMediaOrder, setSocialMediaOrder] = useState<SocialMediaPlatform[]>([]);
  const [content, setContent] = useState({
    hero_name: "",
    hero_title: "",
    hero_description: "",
    hero_followers: "",
    hero_posts: "",
    hero_likes: "",
    hero_instagram_url: "",
    hero_youtube_url: "",
    hero_tiktok_url: "",
    hero_twitter_url: "",
    hero_facebook_url: "",
    hero_linkedin_url: "",
    hero_email: "",
    hero_image: "",
    hero_stats_visible: "true",
  });

  useEffect(() => {
    const fetchContent = async () => {
      const [contentResult, orderResult] = await Promise.all([
        supabase
          .from("site_content")
          .select("content_key, content_value, content_value_en, content_value_es")
          .in("content_key", [
            "hero_name",
            "hero_title",
            "hero_description",
            "hero_followers",
            "hero_posts",
            "hero_likes",
            "hero_instagram_url",
            "hero_youtube_url",
            "hero_tiktok_url",
            "hero_twitter_url",
            "hero_facebook_url",
            "hero_linkedin_url",
            "hero_email",
            "hero_image",
            "hero_stats_visible",
          ]),
        supabase
          .from("social_media_order")
          .select("*")
          .order("order_position", { ascending: true })
      ]);

      if (contentResult.data) {
        const transformed = transformContent(contentResult.data);
        setContent({
          hero_name: transformed.hero_name || "",
          hero_title: transformed.hero_title || "",
          hero_description: transformed.hero_description || "",
          hero_followers: transformed.hero_followers || "",
          hero_posts: transformed.hero_posts || "",
          hero_likes: transformed.hero_likes || "",
          hero_instagram_url: transformed.hero_instagram_url || "",
          hero_youtube_url: transformed.hero_youtube_url || "",
          hero_tiktok_url: transformed.hero_tiktok_url || "",
          hero_twitter_url: transformed.hero_twitter_url || "",
          hero_facebook_url: transformed.hero_facebook_url || "",
          hero_linkedin_url: transformed.hero_linkedin_url || "",
          hero_email: transformed.hero_email || "",
          hero_image: transformed.hero_image || "",
          hero_stats_visible: transformed.hero_stats_visible || "true",
        });
      }

      if (orderResult.data) {
        setSocialMediaOrder(orderResult.data);
      }

      setIsLoaded(true);
    };

    fetchContent();

    // Defer subscription setup to after initial render completes (improves LCP)
    const subscriptionTimeout = setTimeout(() => {
      // Subscribe to real-time updates for site_content changes
      const contentChannel = supabase.channel(`hero-content-changes-${Math.random().toString(36).slice(2)}`, {
        config: {
          broadcast: { self: true }
        }
      });

      contentChannel
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'site_content',
            filter: 'content_key=in.(hero_name,hero_title,hero_description,hero_followers,hero_posts,hero_likes,hero_instagram_url,hero_youtube_url,hero_tiktok_url,hero_twitter_url,hero_facebook_url,hero_linkedin_url,hero_email,hero_image,hero_stats_visible)'
          },
          (payload) => {
            fetchContent();
          }
        )
        .subscribe();

      // Subscribe to real-time updates for social media order changes
      const orderChannel = supabase.channel(`hero-social-order-changes-${Math.random().toString(36).slice(2)}`, {
        config: {
          broadcast: { self: true }
        }
      });

      orderChannel
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'social_media_order'
          },
          (payload) => {
            fetchContent();
          }
        )
        .subscribe();
    }, 1000); // Defer subscriptions by 1 second

    return () => {
      clearTimeout(subscriptionTimeout);
    };
  }, [i18n.language]);
  return <section className="relative py-32 px-1 md:px-4 overflow-hidden">
      {/* Background gradient effect */}
      <div className="absolute inset-0 opacity-5 gradient-instagram"></div>
      <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background"></div>
      
      <div className="container max-w-5xl mx-auto relative z-10">
        <div className="flex flex-col md:flex-row items-center gap-16">
          {/* Profile Image */}
          <div className="relative" style={{ willChange: 'auto' }}>
            <div className="absolute -inset-2 gradient-instagram rounded-full opacity-20"></div>
            <picture>
              <source
                type="image/webp"
                srcSet="/profile-hero.webp"
              />
              <img
                src={content.hero_image || '/profile-hero.jpg'}
                alt={content.hero_name ? `${content.hero_name} profile photo` : ''}
                loading="eager"
                {...{ fetchpriority: "high" }}
                width="224"
                height="224"
                decoding="sync"
                className="relative w-48 h-48 md:w-56 md:h-56 rounded-full object-cover border-4 border-background shadow-2xl"
                style={{ objectPosition: 'center 20%' }}
              />
            </picture>
          </div>

          {/* Profile Info */}
          <div className="flex-1 text-center md:text-left space-y-6"
               style={{ willChange: 'auto' }}>
            <div className="min-h-[200px]">
              <h1 className="text-5xl md:text-6xl font-bold mb-3 tracking-tight min-h-[3.75rem] md:min-h-[4.5rem]">{content.hero_name}</h1>
              <p className="text-2xl font-medium mb-3 min-h-[2rem]">
                <span className="gradient-text">{content.hero_title}</span>
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-xl min-h-[3.5rem]">
                {content.hero_description}
              </p>
            </div>

            {/* Stats */}
            {content.hero_stats_visible === "true" && content.hero_followers && (
              <div className="flex gap-12 justify-center md:justify-start">
                <div className="group">
                  <div className="text-3xl font-bold mb-1 group-hover:scale-110 transition-transform">{content.hero_followers}</div>
                  <div className="text-sm text-muted-foreground font-medium">{t('hero.followers')}</div>
                </div>
                <div className="group">
                  <div className="text-3xl font-bold mb-1 group-hover:scale-110 transition-transform">{content.hero_posts}</div>
                  <div className="text-sm text-muted-foreground font-medium">{t('hero.views')}</div>
                </div>
                <div className="group">
                  <div className="text-3xl font-bold mb-1 group-hover:scale-110 transition-transform">{content.hero_likes}</div>
                  <div className="text-sm text-muted-foreground font-medium">{t('hero.likes')}</div>
                </div>
              </div>
            )}

            {/* CTA Buttons */}
            <div className="min-h-[44px] flex flex-wrap gap-4 justify-center md:justify-start -mx-2 md:mx-0">
              {socialMediaOrder
                .filter(platform => platform.is_visible)
                .map((platform) => {
                  const urlKey = `hero_${platform.platform_name}_url` as keyof typeof content;
                  const url = content[urlKey];

                  if (!url) return null;

                  const renderButton = () => {
                    const buttonVariant = platform.is_featured ? "gradient" : "outline";
                    const buttonClassName = platform.is_featured 
                      ? "hover:shadow-glow transition-all duration-300"
                      : "hover:border-accent hover:text-white transition-all duration-300";
                    
                    const renderIcon = () => {
                      switch (platform.platform_name) {
                        case 'instagram':
                          return <Instagram className="w-5 h-5" />;
                        case 'youtube':
                          return <Youtube className="w-5 h-5" />;
                        case 'tiktok':
                          return <TikTokIcon />;
                        case 'twitter':
                          return <XIcon />;
                        case 'facebook':
                          return <Facebook className="w-5 h-5" />;
                        case 'linkedin':
                          return <Linkedin className="w-5 h-5" />;
                        default:
                          return null;
                      }
                    };

                    const platformLabel = platform.platform_name.charAt(0).toUpperCase() + platform.platform_name.slice(1);
                    
                    return (
                      <Button variant={buttonVariant} size="lg" className={buttonClassName} asChild>
                        <a href={url} target="_blank" rel="noopener noreferrer" onClick={() => trackClick('social', `Hero ${platformLabel}`, url)}>
                          {renderIcon()}
                          {platformLabel}
                        </a>
                      </Button>
                    );
                  };

                  return <div key={platform.platform_name}>{renderButton()}</div>;
                })}
              {content.hero_email && (
                <Button
                  variant="ghost"
                  size="lg"
                  className="hover:bg-secondary hover:text-accent transition-all duration-300"
                  onClick={() => {
                    trackClick('cta', 'Hero Contact Button');
                    onContactClick();
                  }}
                >
                  <Mail className="w-5 h-5" />
                  {t('hero.contact')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>;
};